class NSSCarousel extends HTMLElement {
  constructor() {
    super();

    this.debug = false;

    this.swiper;
    this.settings;
  }

  connectedCallback() {
    if (this.debug) console.log("[NSS][Carousel] Connected.");

    this.initCarousel();

    this.addEventListener(`click`, this.handleClick.bind(this));

    const editorEvents = [
      `shopify:inspector:activate`,
      `shopify:inspector:deactivate`,
      `shopify:section:load`,
      `shopify:section:unload`,
      `shopify:section:select`,
      `shopify:section:deselect`,
      `shopify:section:reorder`,
      `shopify:block:select`,
      `shopify:block:deselect`,
    ];
    editorEvents.forEach((event) => {
      this.addEventListener(event, this.handleEditorEvent.bind(this));
    });
  }

  handleClick(e) {
    if (e.target.closest(`[nss-control-start]`)) {
      this.swiper.slideTo(0);
    }

    if (e.target.closest(`[nss-control-end]`)) {
      let count = this.swiper.slides.length;
      this.swiper.slideTo(count - 1);
    }

    const videoSlideEl = e.target.closest(`[nss-video-playing]`);
    if (videoSlideEl) {
      if (this.debug) console.log("[NSS][Carousel] Clicked video!");

      if (e.target.closest(`.nss-carousel-slide-video__chip`)) return;

      // Modal-based video slides (nss-carousel-slide-video) open a shared
      // nss-drawer with sound instead of toggling playback in place.
      const modalId = videoSlideEl.getAttribute(`data-video-modal`);
      if (modalId) {
        const drawerEl = document.querySelector(
          `nss-drawer[nss-drawer-id="${CSS.escape(modalId)}"]`
        );
        if (drawerEl) drawerEl.open();
        return;
      }

      // First slide starts muted-autoplaying: the first click just turns the
      // sound on (keeps it playing). Subsequent clicks are normal play/pause.
      if (videoSlideEl.getAttribute(`nss-video-muted-autoplay`) === `true`) {
        videoSlideEl.removeAttribute(`nss-video-muted-autoplay`);
        const firstVideoEl = videoSlideEl.querySelector(`video`);
        if (firstVideoEl) {
          firstVideoEl.muted = false;
          firstVideoEl.volume = 0.2;
          if (firstVideoEl.paused) firstVideoEl.play();
        }
        videoSlideEl.setAttribute(`nss-video-playing`, true);
        if (typeof this.settings.autoplay !== `undefined`) {
          this.swiper.autoplay.stop();
        }
        return;
      }

      if (videoSlideEl.getAttribute(`nss-video-playing`) === `false`) {
        if (this.debug) console.log("[NSS][Carousel] Playing video!");
        this.stopAllVideos();
        const videoEl = videoSlideEl.querySelector(`video`);
        if (!videoEl) return;
        videoSlideEl.setAttribute(`nss-video-playing`, true);
        videoEl.volume = 0.2;
        videoEl.muted = false;
        videoEl.play();
        if (typeof this.settings.autoplay !== `undefined`) {
          this.swiper.autoplay.stop();
        }
      } else {
        if (this.debug) console.log("[NSS][Carousel] Not playing video!");
        this.stopAllVideos();
        if (typeof this.settings.autoplay !== `undefined`) {
          this.swiper.autoplay.start();
        }
      }
    }
  }

  stopAllVideos() {
    this.querySelectorAll(`[nss-video-playing]`).forEach((videoSlideEl) => {
      const videoEl = videoSlideEl.querySelector(`video`);
      if (videoEl) {
        videoEl.pause();
        videoEl.muted = true;
      }
      videoSlideEl.setAttribute(`nss-video-playing`, false);
      videoSlideEl.removeAttribute(`nss-video-muted-autoplay`);
    });
  }

  initCarousel() {
    const carouselEl = this.querySelector(`.swiper`);
    const settings = JSON.parse(this.querySelector(`#swiper-settings`).textContent);

    const wrapperEl = carouselEl.querySelector(`.swiper-wrapper`);
    [...wrapperEl.children].forEach((childEl) => {
      if ([`LINK`, `SCRIPT`, `STYLE`, `TEMPLATE`].includes(childEl.tagName)) {
        childEl.classList.remove(`swiper-slide`);
        return;
      }
      childEl.classList.add(`swiper-slide`);
    });

    // if (this.debug) console.log("[NSS][Carousel] settings:", settings);

    const presets = {
      a11y: {
        enabled: true,
        // Slides are <li> inside <ul class="swiper-wrapper">; Swiper's default role="group" breaks the list.
        slideRole: null,
      },
      navigation: {
        prevEl: ".nss-carousel-button-prev",
        nextEl: ".nss-carousel-button-next",
      },
      pagination: {
        el: ".swiper-pagination",
        type: "bullets",
        clickable: true,
      },
      scrollbar: {
        el: ".swiper-scrollbar",
        draggable: true,
      },
      grabCursor: true,
    };

    this.settings = Object.assign(presets, settings);
    if (this.debug) console.log("[NSS][Carousel] this.settings:", this.settings);

    this.swiper = new Swiper(carouselEl, this.settings);

    this.swiper.on(`reachBeginning`, this.handleReachBeginning.bind(this));
    this.swiper.on(`reachEnd`, this.handleReachEnd.bind(this));
    this.swiper.on(`slideChange`, this.handleSlideChange.bind(this));

    this.paginationGroup = this.querySelector(`.nss-carousel__pagination-group`);
    this.syncPagination();
    this.swiper.on(`resize`, this.syncPagination.bind(this));
    this.swiper.on(`update`, this.syncPagination.bind(this));
    this.swiper.on(`breakpoint`, this.syncPagination.bind(this));

    this.autoplayFirstVideo();
  }

  autoplayFirstVideo() {
    const firstVideoSlide =
      this.querySelector(`.swiper-slide:not(.swiper-slide-duplicate) [nss-video-playing]`) ||
      this.querySelector(`[nss-video-playing]`);
    if (!firstVideoSlide) return;
    const videoEl = firstVideoSlide.querySelector(`video`);
    if (!videoEl) return;

    // Autoplay the first video muted while keeping nss-video-playing="false"
    // so the play button stays visible; the first click unmutes it.
    videoEl.muted = true;
    firstVideoSlide.setAttribute(`nss-video-muted-autoplay`, true);
    const played = videoEl.play();
    if (played && typeof played.catch === `function`) played.catch(() => {});
  }

  syncPagination() {
    if (!this.paginationGroup || !this.swiper) return;
    const scrollable =
      !this.swiper.isLocked && !(this.swiper.isBeginning && this.swiper.isEnd);
    this.paginationGroup.style.display = scrollable ? `` : `none`;
  }

  handleSlideChange() {
    // const advisorEls = this.querySelectorAll(`nss-carousel-slide-advisor`);
    // advisorEls.forEach((advisorEl) => {
    //   advisorEl.parentElement.classList.remove(`nss-active`);
    // });
    this.swiper.update();
  }

  handleReachBeginning() {
    if (this.debug) console.log(`[NSS][Carousel] Reached beginning!`);

    const controlsEl = this.querySelector(`nss-start-end-controls`);
    const startEl = this.querySelector(`[nss-control-start]`);
    const endEl = this.querySelector(`[nss-control-end]`);
    if (controlsEl) {
      startEl.classList.add(`nss-active`);
      endEl.classList.remove(`nss-active`);
      controlsEl.setAttribute(`nss-position`, `start`);
    }
  }

  handleReachEnd() {
    if (this.debug) console.log(`[NSS][Carousel] Reached end!`);

    const controlsEl = this.querySelector(`nss-start-end-controls`);
    const startEl = this.querySelector(`[nss-control-start]`);
    const endEl = this.querySelector(`[nss-control-end]`);
    if (controlsEl) {
      startEl.classList.remove(`nss-active`);
      endEl.classList.add(`nss-active`);
      controlsEl.setAttribute(`nss-position`, `end`);
    }
  }

  handleEditorEvent(e) {
    if (this.debug) console.log("[NSS][Carousel] Handle editor event, e:", e);

    switch (e.type) {
      case `shopify:block:select`:
        const blockEl = this.querySelector(`[data-block-id="${e.detail.blockId}"]`);
        const slideEl = blockEl.closest(`.swiper-slide`);
        if (this.debug) console.log("[NSS][Carousel] slideEl:", slideEl);
        if (slideEl) {
          // const index = slideEl.getAttribute(`data-swiper-slide-index`);
          const slideEls = [...this.querySelectorAll(`.swiper-slide`)];
          const index = slideEls.indexOf(slideEl);
          if (this.debug) console.log("[NSS][Carousel] Sliding to index:", index);
          this.swiper.slideTo(index);
        }
        break;
    }
  }
}

let elementName = `nss-carousel`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSCarousel);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`);
}
